import React from 'react';
import { Tag } from '../molecules/NewQuestionForm/atoms/Tag';
import {
    alertMessages,
    difficultyOptions,
    titlesAndDescriptions,
    languages,
    numbers,
    questionTypes,
} from '../variables/variables';
import { FaTimesCircle } from 'react-icons/fa';
import {
    QTYPE_TEXT,
    QTYPE_MULTI_CHOICE,
    QTYPE_MULTI_ANSWER,
    QTYPE_NUMBER,
    QTYPE_CAMERA,
    QTYPE_PRACTICAL,
    QTYPE_P_NUMBER,
    QTYPE_P_TEXT,
    QTYPE_P_BOOL,
} from '../../constants/questions';

export const isValidType = (value) => {
  return (typeof value === 'string' || typeof value === 'number');
};

export const answerParser = (question) => {
  const AnswerText = ({ answerText, answerType }) => (
    <p className={'question-answer-text-' + answerType}>{answerText}</p>
  );

  if (question.answer) {
    let output = [(
        <p className="question-answer-text" key={'qans_'+question.id}>
            {titlesAndDescriptions.answerLabel}
        </p>
    )];

    if (isValidType(question.answer)) {
      if (question.alternatives) {
        if (Array.isArray(question.alternatives)) {
          let alternativesAreValid = true;
          for (let i = 0; i < question.alternatives.length; i++) {
            const validAlternative = isValidType(question.alternatives[i]);
            if (!validAlternative) {
              alternativesAreValid = false;
              break;
            }
          }

          if (!alternativesAreValid) {
            output.push(
              <AnswerText
                key={'qans_'+question.id+'_err'}
                answerType={'error'}
                answerText={alertMessages.errorAnswerFormat}
               />,
            );
          } else {
            for (let i = 0; i < question.alternatives.length; i++) {
              //loose equality in case some string conversion has happened etc.
              const isCorrect = (question.answer === question.alternatives[i]);
              output.push(
                <AnswerText
                  key={'qans_'+question.id+'_alt_'+i}
                  answerType={isCorrect ? 'correct' : 'incorrect'}
                  answerText={question.alternatives[i]}
                 />,
              );
            }
          }
        } else if (isValidType(question.alternatives)) {
          const isCorrect = question.alternatives === question.answer; // loose equality in case some string conversion has happened etc.
          if (isCorrect) {
            output.push(
              <AnswerText
                key={'qans_'+question.id+'_corr'}
                answerType={'correct'}
                answerText={question.answer}
               />,
            );
          } else {
            output.push(
              <AnswerText
                key={'qans_'+question.id+'_err'}
                answerType={'error'}
                answerText={alertMessages.errorCorrectMissing}
               />,
            );
          }
        } else {
          output.push(
            <AnswerText
              key={'qans_'+question.id+'_err'}
              answerType={'error'}
              answerText={alertMessages.errorAnswerFormat}
             />,
          );
        }
      } else {
        output.push(
          <AnswerText
            key={'qans_'+question.id+'_ans'}
            answerType={'correct'}
            answerText={question.answer}
           />,
        );
      }
    }

    if (Array.isArray(question.answer)) {
      let answersAreValid = true;
      for (let i = 0; i < question.answer.length; i++) {
        const validAnswer = isValidType(question.answer[i]);
        if (!validAnswer) {
          answersAreValid = false;
          break;
        }
      }
      if (!answersAreValid) {
        output.push(
          <p style={{ margin: '0 1rem', color: '#000000' }}>
            {alertMessages.errorAnswerFormat}
          </p>,
        );
      } else if (question.alternatives) {
        if (Array.isArray(question.alternatives)) {
          let alternativesAreValid = true;
          for (let i = 0; i < question.alternatives.length; i++) {
            const validAlternative = isValidType(question.alternatives[i]);
            if (!validAlternative) {
              alternativesAreValid = false;
              break;
            }
          }

          if (!alternativesAreValid) {
            output.push(
              <AnswerText
                key={'qans_'+question.id+'_err'}
                answerType={'error'}
                answerText={alertMessages.errorAnswerFormat}
               />,
            );
          } else {
            for (let i = 0; i < question.alternatives.length; i++) {
              const isCorrect = question.answer.some(
                (ans) => ans === question.alternatives[i],
              );
              output.push(
                <AnswerText
                  key={'qans_'+question.id+'_alt_'+i}
                  answerType={isCorrect ? 'correct' : 'incorrect'}
                  answerText={question.alternatives[i]}
                 />,
              );
            }
          }
        } else if (isValidType(question.alternatives)) {
          if (
            question.answer.some(
              (ans) => ans === question.alternatives,
            )
          ) {
            for (let i = 0; i < question.answer.length; i++) {
              const isCorrect = (question.answer[i] === question.alternatives);
              output.push(
                <AnswerText
                  key={'qans_'+question.id+'_alt_'+i}
                  answerType={isCorrect ? 'correct' : 'incorrect'}
                  answerText={question.answer[i]}
                 />,
              );
            }
          } else {
            output.push(
              <AnswerText
                key={'qans_'+question.id+'_err'}
                answerType={'error'}
                answerText={alertMessages.errorNoMatchingAlternative}
               />,
            );
          }
        } else {
          output.push(
            <AnswerText
              key={'qans_'+question.id+'_err'}
              answerType={'error'}
              answerText={alertMessages.errorAnswerFormat}
             />,
          );
        }
      } else {
        for (let i = 0; i < question.answer.length; i++) {
          output.push(
            <AnswerText
              key={'qans_'+question.id+'_alt_'+i}
              answerType={'correct'}
              answerText={question.answer[i]}
             />,
          );
        }
      }
    }
    return output;
  }
  return null;
};

export const getAnswer = (user, question) => {
  if (!user.answers) {
    if (question.type === QTYPE_CAMERA) {
      return FaTimesCircle;
    } else {
      return titlesAndDescriptions.noResponseLabel;
    }
  }
  const ans = user.answers.find((a) => a.questionId === question.id);

  if (ans) {
    if (ans.answer instanceof Array) {
      return ans.answer.join(", ");
    } else {
      return ans.answer;
    }
  } else {
    if (question.type === QTYPE_CAMERA) {
      return FaTimesCircle;
    } else {
      return titlesAndDescriptions.noResponseLabel;
    }
  }
};

export const formatAnswers = (type, answer, alternatives, question) => {
  if (type === QTYPE_TEXT) {
    return (
      <div className="question-see-more-simple-answer">
        {titlesAndDescriptions.correctAnswerLabel + answer}
      </div>
    );
  }

  if (type === QTYPE_MULTI_CHOICE) {
    return (
      <div className="question-see-more-multiple-choice-answer">
        <table>
          <thead>
            <tr>
              <th className="alternatives-header" colSpan={alternatives.length}>
                {titlesAndDescriptions.alternativesLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr key={'mc_alternatives_'+question.id}>
            {alternatives.map((a, i) => {
              return (
                <td className={'multiple-choice-row'+(answer.includes(a)?' correct':' wrong')}>
                  {a}
                </td>
              );
            })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  if (type === QTYPE_MULTI_ANSWER) {
    return (
      <div className="question-see-more-multiple-answer-answer">
        <table>
          <caption></caption>
          <thead>
            <tr>
              <th className="alternatives-header" colSpan={alternatives.length}>
                {titlesAndDescriptions.alternativesLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr key={'ma_alternatives_'+question.id}>
            {alternatives.map((a, i) => {
              return (
                <td key={'ma_alternatives_'+i} className={'multiple-choice-row'+(answer.includes(a)?' correct':' wrong')}>
                  {a}
                </td>
              );
            })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  if (type === QTYPE_NUMBER) {
    return (
      <div className="question-see-more-number-answer">
        <div className="question-see-more-number-answer">
          {titlesAndDescriptions.correctAnswerLabel + answer}
        </div>
      </div>
    );
  }

  if (type === QTYPE_PRACTICAL) {
    return <div className="question-see-more-practical-answer"></div>;
  }
};

export const questionTypeParser = (type) => {
  switch (type) {
    case QTYPE_TEXT:
      return questionTypes.simpleText;
    case QTYPE_MULTI_CHOICE:
      return questionTypes.multipleChoice;
    case QTYPE_NUMBER:
      return questionTypes.simpleNumber;
    case QTYPE_PRACTICAL:
      return questionTypes.practicalAssignment;
    case QTYPE_MULTI_ANSWER:
      return questionTypes.multipleAnswer;
    case QTYPE_CAMERA:
      return questionTypes.photoAssignment;
    default:
      return questionTypes.unknownType;
  }
};

export const optionsPractical = [
  { label: questionTypes.simpleNumber, value: QTYPE_P_NUMBER },
  { label: questionTypes.simpleText, value: QTYPE_P_TEXT },
  { label: questionTypes.boolSelection, value: QTYPE_P_BOOL },
];

export const qTypeParserPractical = (type) => {
  switch (type) {
    case QTYPE_P_NUMBER:
      return { label: questionTypes.simpleNumber, value: QTYPE_P_NUMBER };
    case QTYPE_P_TEXT:
      return { label: questionTypes.simpleText, value: QTYPE_P_TEXT };
    case QTYPE_P_BOOL:
      return { label: questionTypes.boolSelection, value: QTYPE_P_BOOL };
    default:
      return '';
  }
};

export const gNameParser = (name) => {
  if (name) {
    return name;
  } else {
    return alertMessages.errorDataNotFound;
  }
};

export const gCreatedParser = (e) => {
  if (typeof e.created != 'undefined') {
    const today = e.created.toDate();
    return (
      String(today.getDate()).padStart(2, '0') +
      '.' +
      String(today.getMonth() + 1).padStart(2, '0') +
      '.' +
      today.getFullYear()
    );
  }
  return '-';
};

export const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (var i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const getNumberAsString = (value, max) => {
  if (value < 0) {
    return "";
  }
  if (value > numbers.length || (max && value > max)) {
    return titlesAndDescriptions.manyLabel;
  }
  return numbers[value];
};

export const subjectParser = (subject) => {
  if (!subject || subject === '') {
    return { label: titlesAndDescriptions.allLabel, value: titlesAndDescriptions.allLabel };
  }
  return { label: subject, value: subject };
};

export const subjectFilter = (list, subject) => {
  if (subject === titlesAndDescriptions.allLabel) {
    return list;
  }
  return list.filter((e) => e.category.subject === subject);
};

export const difficultyParser = (difficulty) => {
  if (difficulty === 0) {
    return { label: titlesAndDescriptions.allLabel, value: difficulty };
  }
  if (difficulty === 30) {
    return { label: titlesAndDescriptions.otherLabel, value: difficulty };
  }
  let newDifficulty = undefined;
  difficultyOptions.forEach((value) => {
    if (value.value === difficulty) {
      newDifficulty = { label: value.label, value: difficulty };
    }
  });
  return newDifficulty ?? { label: titlesAndDescriptions.allLabel, value: 0 };
};

export const difficultyFilter = (list, difficulty) => {
  if (difficulty === 0) {
    return list;
  }
  return list.filter((e) => e.category.grade === difficulty);
};

export const difficultyValueParser = (difficulty) => {
  difficultyOptions.forEach((value) => {
    if (value.value === difficulty) {
      return value.label;
    }
  });
  return difficultyOptions[0].label;
};

export const tagParser = (tags) => {
  if (tags && tags.length > 0) {
    const tagText = tags.map((t, i) => (
      <Tag key={'tag_key_' + i} text={t} checked={false} handler={() => { /* Do nothing */ }} />
    ));
    if (tagText.length > 0) {
      return ( tagText );
    }
    return (
      <span className="no-tags no-tags-label">{titlesAndDescriptions.noTagsLabel}</span>
    );
  }
  return null;
};

export const tagFilter = (list, tags) => {
  if (!tags || tags.length === 0) {
    return list;
  }
  return list.filter((e) => e.tags.some((t) => tags.includes(t)));
};

export const languageFilter = (list, bokmal, sidemal) => {
  if (bokmal) {
    return list.filter((e) => (e.language ?? languages.bokmaal) === languages.bokmaal);
  } else if (sidemal) {
    return list.filter((e) => (e.language ?? languages.bokmaal) === languages.nynorsk);
  }
  return list;
};
